<template>
	<v-dialog
		v-model="dialog"
		max-width="800"
		persistent
		scrollable
	>
		<v-card>
			<v-card-title class="d-flex justify-space-between align-center">
				<span style="font-size: 24px; font-weight: 700; color: #141414;">
					Configurar Membros
				</span>
				<v-btn
					icon
					@click="closeDialog"
					:disabled="loading"
				>
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-card-title>

			<v-card-text>
				<p style="color: #4d4d4d; margin-bottom: 24px; font-size: 15px;">
					Adicione membros ao seu grupo de Eleitores. Você pode adicionar usuários já cadastrados ou gerar um link para novos usuários.
				</p>

				<!-- Campo de busca -->
				<div class="d-flex align-center mb-3">
					<v-text-field
						v-model="searchUser"
						label="Buscar por nome ou e-mail"
						placeholder="Digite o nome ou e-mail do integrante"
						prepend-inner-icon="mdi-magnify"
						color="#F15A24"
						filled
						hide-details
						clearable
						class="mr-2"
						@keyup.enter="filterUsers"
					/>
					<v-btn
						color="#F15A24"
						dark
						:loading="loadingUsers"
						:disabled="!searchUser || searchUser.trim().length < 2"
						@click="filterUsers"
					>
						<v-icon>mdi-magnify</v-icon>
					</v-btn>
				</div>

				<!-- Lista de usuários encontrados -->
				<v-list
					v-if="filteredUsers.length > 0"
					class="mb-3"
					style="max-height: 300px; overflow-y: auto;"
				>
					<v-list-item
						v-for="user in filteredUsers"
						:key="user.id"
						class="chip-with-hover"
						style="padding: 0.5rem; margin-bottom: 0.5rem; border: 1px solid #e0e0e0; border-radius: 8px;"
					>
						<v-list-item-icon class="mr-3">
							<v-icon
								v-if="!user.avatar"
								size="40px"
								color="#F15A24"
							>
								mdi-account
							</v-icon>
							<v-img
								v-if="user.avatar"
								width="40px"
								height="40px"
								class="rounded-circle"
								:src="`https://api.mylegis.com.br/assets/${user.avatar}`"
							/>
						</v-list-item-icon>
						<v-list-item-content>
							<v-list-item-title>
								{{ user.first_name }} {{ user.last_name || '' }}
							</v-list-item-title>
							<v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
						</v-list-item-content>
						<v-list-item-action>
							<v-btn
								color="#F15A24"
								dark
								small
								@click="adicionarUsuario(user)"
								:disabled="user.adicionado || adicionandoUsuario"
							>
								<v-icon left small v-if="!user.adicionado">mdi-plus</v-icon>
								{{ user.adicionado ? 'Adicionado' : 'Adicionar' }}
							</v-btn>
						</v-list-item-action>
					</v-list-item>
				</v-list>

				<!-- Mensagem quando não encontrou usuários -->
				<v-alert
					v-if="searched && filteredUsers.length === 0"
					type="info"
					dense
					text
					class="mb-3"
				>
					Usuário não encontrado. 
					<span
						style="color: #F15A24; cursor: pointer; font-weight: 600; text-decoration: underline;"
						@click="mostrarFormularioConvite = true"
					>
						Gerar link de convite
					</span>
				</v-alert>

				<!-- Seção de Lideranças -->
				<v-divider class="my-4"></v-divider>
				<h4 style="font-size: 16px; font-weight: 600; color: #141414; margin-bottom: 12px;">
					Convidar Lideranças
				</h4>
				<p style="color: #4d4d4d; font-size: 14px; margin-bottom: 12px;">
					Convide pessoas com plano Legislativo para serem lideranças do seu grupo. 
					As publicações do grupo delas serão compartilhadas também no seu grupo.
				</p>

				<!-- Lista de usuários encontrados que podem ser lideranças -->
				<v-list
					v-if="usuariosLideranca.length > 0"
					class="mb-3"
					style="max-height: 200px; overflow-y: auto;"
				>
					<v-list-item
						v-for="user in usuariosLideranca"
						:key="`lider-${user.id}`"
						class="chip-with-hover"
						style="padding: 0.5rem; margin-bottom: 0.5rem; border: 1px solid #e0e0e0; border-radius: 8px;"
					>
						<v-list-item-icon class="mr-3">
							<v-icon
								v-if="!user.avatar"
								size="40px"
								color="#F15A24"
							>
								mdi-account-star
							</v-icon>
							<v-img
								v-if="user.avatar"
								width="40px"
								height="40px"
								class="rounded-circle"
								:src="`https://api.mylegis.com.br/assets/${user.avatar}`"
							/>
						</v-list-item-icon>
						<v-list-item-content>
							<v-list-item-title>
								{{ user.first_name }} {{ user.last_name || '' }}
								<v-chip x-small color="orange" text-color="white" class="ml-2">
									Plano Legislativo
								</v-chip>
							</v-list-item-title>
							<v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
						</v-list-item-content>
						<v-list-item-action>
							<v-btn
								color="#F15A24"
								dark
								small
								@click="convidarLideranca(user)"
								:disabled="user.liderancaConvidada || convidandoLideranca"
							>
								<v-icon left small v-if="!user.liderancaConvidada">mdi-account-star</v-icon>
								{{ user.liderancaConvidada ? 'Convite Enviado' : 'Convidar' }}
							</v-btn>
						</v-list-item-action>
					</v-list-item>
				</v-list>

				<v-alert
					v-if="searched && usuariosLideranca.length === 0 && filteredUsers.length > 0"
					type="info"
					dense
					text
					class="mb-3"
				>
					Nenhum usuário encontrado com plano Legislativo para convidar como liderança.
				</v-alert>

				<!-- Formulário de geração de link de convite -->
				<v-expand-transition>
					<v-card
						v-if="mostrarFormularioConvite"
						class="mt-4"
						elevation="2"
						style="background-color: #f5f5f5;"
					>
						<v-card-text>
							<div class="d-flex justify-space-between align-center mb-3">
								<h4 style="font-size: 16px; font-weight: 600; color: #141414;">
									Gerar link de convite
								</h4>
								<v-btn
									icon
									small
									@click="mostrarFormularioConvite = false"
								>
									<v-icon>mdi-close</v-icon>
								</v-btn>
							</div>
							<p style="color: #4d4d4d; font-size: 14px; margin-bottom: 16px;">
								Clique no botão abaixo para gerar um link de convite. Compartilhe este link com o integrante do grupo.
							</p>
							<v-btn
								color="#F15A24"
								dark
								block
								@click="gerarLinkConvite"
								:loading="gerandoLink"
							>
								<v-icon left>mdi-link-variant</v-icon>
								Gerar Link de Convite
							</v-btn>

							<!-- Exibir link gerado -->
							<v-expand-transition>
								<div v-if="linkConviteGerado" class="mt-4">
									<v-text-field
										:value="linkConviteGerado"
										label="Link de convite"
										readonly
										prepend-inner-icon="mdi-link"
										color="#F15A24"
										filled
										hide-details
										class="mb-3"
									>
										<template v-slot:append>
											<v-btn
												icon
												small
												@click="copiarLink"
												:disabled="copiandoLink"
											>
												<v-icon>{{ linkCopiado ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
											</v-btn>
										</template>
									</v-text-field>
									<v-alert
										v-if="linkCopiado"
										type="success"
										dense
										text
										class="mb-0"
									>
										Link copiado para a área de transferência!
									</v-alert>
								</div>
							</v-expand-transition>
						</v-card-text>
					</v-card>
				</v-expand-transition>
			</v-card-text>

			<v-card-actions class="d-flex justify-space-between">
				<v-btn
					text
					@click="closeDialog"
					:disabled="loading"
				>
					Cancelar
				</v-btn>
				<v-btn
					color="#F15A24"
					dark
					@click="closeDialog"
					:disabled="loading"
				>
					Fechar
				</v-btn>
			</v-card-actions>

			<v-snackbar
				v-model="snackbar"
				:color="snackbarColor"
				timeout="3000"
				top
			>
				{{ snackbarText }}
				<template v-slot:action="{ attrs }">
					<v-btn
						text
						v-bind="attrs"
						@click="snackbar = false"
					>
						Fechar
					</v-btn>
				</template>
			</v-snackbar>
		</v-card>
	</v-dialog>
</template>

<script>
export default {
	name: 'DialogConfigurarLiderancas',
	props: {
		value: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			dialog: this.value,
			gerandoLink: false,
			linkConviteGerado: '',
			copiandoLink: false,
			linkCopiado: false,
			loading: false,
			snackbar: false,
			snackbarText: '',
			snackbarColor: 'success',
			// Busca de usuários
			searchUser: '',
			filteredUsers: [],
			loadingUsers: false,
			searched: false,
			adicionandoUsuario: false,
			mostrarFormularioConvite: false,
			integrantesIds: [],
			usuariosLideranca: [],
			convidandoLideranca: false,
		};
	},
	computed: {
		users() {
			return this.$store.state.users;
		},
		grupoId() {
			return this.$route.params.id_grupo;
		},
	},
	watch: {
		value(newVal) {
			this.dialog = newVal;
			if (newVal) {
				this.carregarIntegrantesExistentes();
			} else {
				// Limpar campos quando fechar o dialog
				this.searchUser = '';
				this.filteredUsers = [];
				this.searched = false;
				this.linkConviteGerado = '';
				this.linkCopiado = false;
				this.mostrarFormularioConvite = false;
			}
		},
		dialog(newVal) {
			this.$emit('input', newVal);
		},
	},
	methods: {
		closeDialog() {
			this.dialog = false;
		},
		mostrarSnackbar(text, color = 'success') {
			this.snackbarText = text;
			this.snackbarColor = color;
			this.snackbar = true;
		},
		temPlanoLegislativo(plano) {
			if (!plano) return false;
			return (
				plano === 'plano-legislativo' ||
				plano === 'plano_legislativo' ||
				plano === 'legislativo' ||
				plano.toLowerCase().includes('legislativo') ||
				plano === 'plan_g3180ooh6OSo0Wzx'
			);
		},
		async carregarIntegrantesExistentes() {
			try {
				if (!this.grupoId) return;
				
				const { data: grupoData } = await this.$http.get(
					`items/grupos/${this.grupoId}?fields=integrantes.directus_users_id.id`
				);
				
				const integrantesIds = (grupoData.data.integrantes || []).map(
					i => i.directus_users_id && i.directus_users_id.id
				).filter(id => id);
				
				// Armazenar IDs dos integrantes para verificação
				this.integrantesIds = integrantesIds;
			} catch (error) {
				// Erro ao carregar integrantes
				this.integrantesIds = [];
			}
		},
		async carregarConvitesLiderancaPendentes() {
			try {
				if (!this.grupoId) return [];
				
				// Buscar todos os convites pendentes deste grupo
				const { data } = await this.$http.get(
					`items/add_grupos?filter[id_grupo][_eq]=${this.grupoId}&filter[status][_eq]=pendente&fields=id_usuario`
				);
				
				// Filtrar apenas convites de liderança
				// Como não podemos filtrar por tipo, vamos verificar se o usuário convidado tem plano legislativo
				// Se tiver, é um convite de liderança
				const convites = data.data || [];
				const convitesLideranca = [];
				
				for (const convite of convites) {
					try {
						const { data: userData } = await this.$http.get(
							`users/${convite.id_usuario}?fields=id,plano`
						);
						
						if (this.temPlanoLegislativo(userData.data.plano)) {
							convitesLideranca.push(convite);
						}
					} catch (err) {
						// Ignorar erros ao buscar usuário
					}
				}
				
				return convitesLideranca;
			} catch (error) {
				return [];
			}
		},
		async filterUsers() {
			if (!this.searchUser || this.searchUser.trim().length < 2) {
				this.filteredUsers = [];
				this.searched = false;
				return;
			}

			this.searched = true;
			this.loadingUsers = true;
			const searchTerm = this.searchUser.trim();

			try {
				// Buscar por nome usando o store
				await this.$store.dispatch('searchUsuarios', searchTerm);
				let usuariosPorNome = this.users.filter(user => 
					user.id !== this.$store.state.user.id
				);

				// Também buscar por email diretamente
				let usuariosPorEmail = [];
				try {
					const { data } = await this.$http.get(
						`users?filter[email][_contains]=${searchTerm}&fields=id,first_name,last_name,email,avatar&limit=10`
					);
					usuariosPorEmail = (data.data || []).filter(user => 
						user.id !== this.$store.state.user.id
					);
				} catch (err) {
					// Erro ao buscar por email
				}

				// Combinar resultados e remover duplicatas
				const todosUsuarios = [...usuariosPorNome, ...usuariosPorEmail];
				const usuariosUnicos = todosUsuarios.filter((user, index, self) =>
					index === self.findIndex(u => u.id === user.id)
				);

				// Buscar informações de plano de todos os usuários
				// Fazer busca individual para cada usuário (mais compatível)
				let usuariosComPlano = [];
				
				try {
					const usuariosComInfo = await Promise.all(
						usuariosUnicos.map(async (user) => {
							try {
								const { data: userData } = await this.$http.get(
									`users/${user.id}?fields=id,plano`
								);
								return {
									...user,
									plano: userData.data.plano,
									temPlanoLegislativo: this.temPlanoLegislativo(userData.data.plano),
								};
							} catch (err) {
								return {
									...user,
									plano: null,
									temPlanoLegislativo: false,
								};
							}
						})
					);
					
					usuariosComPlano = usuariosComInfo;
				} catch (err) {
					// Se falhar, usar dados básicos
					usuariosComPlano = usuariosUnicos.map(user => ({
						...user,
						plano: null,
						temPlanoLegislativo: false,
					}));
				}

				// Verificar quais usuários já estão no grupo
				this.filteredUsers = usuariosComPlano.map(user => ({
					...user,
					adicionado: this.integrantesIds && this.integrantesIds.includes(user.id),
				}));

				// Separar usuários com plano legislativo para lideranças
				// IMPORTANTE: Apenas administradores (não convidados) podem ser lideranças
				// Verificar também se já foram convidados
				const convitesLideranca = await this.carregarConvitesLiderancaPendentes();
				const convitesIds = convitesLideranca.map(c => c.id_usuario);
				
				// Filtrar apenas usuários que são administradores (não convidados)
				// Verificar se o usuário tem grupos onde é o criador (administrador)
				const usuariosAdministradores = await Promise.all(
					usuariosComPlano
						.filter(user => user.temPlanoLegislativo)
						.map(async (user) => {
							try {
								// Buscar grupos do usuário para verificar se ele é administrador
								const { data: gruposData } = await this.$http.get(
									`items/grupos?filter[user_created][_eq]=${user.id}&fields=id,user_created`
								);
								
								// Verificar também se o usuário tem o campo eh_convidado_gabinete
								const { data: userData } = await this.$http.get(
									`users/${user.id}?fields=id,eh_convidado_gabinete,admin_gabinete_id`
								);
								
								// Usuário é administrador se:
								// 1. Tem grupos onde é o criador (user_created = user.id)
								// 2. NÃO é convidado (eh_convidado_gabinete !== true e não tem admin_gabinete_id)
								const ehConvidado = userData.data.eh_convidado_gabinete === true || userData.data.admin_gabinete_id;
								const ehAdministrador = gruposData.data && gruposData.data.length > 0 && !ehConvidado;
								
								return {
									...user,
									ehAdministrador,
									liderancaConvidada: convitesIds.includes(user.id),
								};
							} catch (err) {
								// Em caso de erro, considerar como não administrador
								return {
									...user,
									ehAdministrador: false,
									liderancaConvidada: convitesIds.includes(user.id),
								};
							}
						})
				);
				
				// Filtrar apenas os que são administradores
				this.usuariosLideranca = usuariosAdministradores.filter(user => user.ehAdministrador);
			} catch (error) {
				// Erro ao buscar usuários
				this.mostrarSnackbar('Erro ao buscar usuários. Tente novamente.', 'error');
				this.filteredUsers = [];
			} finally {
				this.loadingUsers = false;
			}
		},
		cleanFields() {
			this.filteredUsers = [];
			this.searchUser = '';
			this.searched = false;
		},
		async adicionarUsuario(user) {
			if (!this.grupoId) {
				this.mostrarSnackbar('Erro: ID do grupo não encontrado.', 'error');
				return;
			}

			// Verificar se já está no grupo
			if (user.adicionado) {
				this.mostrarSnackbar('Este usuário já está no grupo.', 'info');
				return;
			}

			this.adicionandoUsuario = true;

			try {
				// Adicionar usuário diretamente ao grupo usando PATCH (mesmo padrão do DialogIntegrantesGabinete)
				await this.$http.patch(`items/grupos/${this.grupoId}`, {
					integrantes: {
						create: [{
							directus_users_id: { id: user.id },
							grupos_id: this.grupoId
						}]
					}
				});

				user.adicionado = true;
				// Atualizar lista de integrantes
				if (!this.integrantesIds) {
					this.integrantesIds = [];
				}
				this.integrantesIds.push(user.id);
				
				this.mostrarSnackbar(
					`${user.first_name} foi adicionado ao grupo com sucesso!`,
					'success'
				);
				
				// Aguardar um pouco para garantir que o relacionamento foi salvo
				await new Promise(resolve => setTimeout(resolve, 300));
				
				// Recarregar integrantes para atualizar a lista
				await this.carregarIntegrantesExistentes();
				
				// Limpar busca
				this.searchUser = '';
				this.filteredUsers = [];
				this.searched = false;
			} catch (error) {
				// Erro ao adicionar usuário ao grupo
				this.mostrarSnackbar('Erro ao adicionar usuário. Tente novamente.', 'error');
			} finally {
				this.adicionandoUsuario = false;
			}
		},
		gerarLinkConvite() {
			if (this.gerandoLink) return;

			this.gerandoLink = true;
			this.linkCopiado = false;

			try {
				const baseUrl = window.location.origin;
				const grupoId = this.grupoId;

				if (!grupoId) {
					this.mostrarSnackbar('Erro: ID do grupo não encontrado.', 'error');
					return;
				}

				// Gera um identificador simples para tornar o link único no front
				const token = `${grupoId}_${Date.now()}`;

				// Link ajustado para usar /cadastro/plano-free com parâmetros de grupo
				// Isso garante que o usuário seja cadastrado no plano Free e vinculado ao grupo Eleitores
				this.linkConviteGerado = `${baseUrl}/cadastro/plano-free?grupo=${grupoId}&tipo=eleitores&t=${token}`;

				this.mostrarSnackbar('Link de convite gerado com sucesso!', 'success');
			} catch (error) {
				// Erro ao gerar link de convite de eleitores
				this.mostrarSnackbar('Erro ao gerar link. Tente novamente.', 'error');
			} finally {
				this.gerandoLink = false;
			}
		},
		async copiarLink() {
			if (!this.linkConviteGerado) return;

			this.copiandoLink = true;
			try {
				if (navigator.clipboard && navigator.clipboard.writeText) {
					await navigator.clipboard.writeText(this.linkConviteGerado);
				} else {
					const textArea = document.createElement('textarea');
					textArea.value = this.linkConviteGerado;
					textArea.style.position = 'fixed';
					textArea.style.left = '-999999px';
					document.body.appendChild(textArea);
					textArea.select();
					document.execCommand('copy');
					document.body.removeChild(textArea);
				}

				this.linkCopiado = true;
				this.mostrarSnackbar('Link copiado para a área de transferência!', 'success');

				setTimeout(() => {
					this.linkCopiado = false;
				}, 3000);
			} catch (error) {
				// Erro ao copiar link de eleitores
				this.mostrarSnackbar(
					'Erro ao copiar link. Tente selecionar e copiar manualmente.',
					'error'
				);
			} finally {
				this.copiandoLink = false;
			}
		},
		async convidarLideranca(user) {
			if (!this.grupoId) {
				this.mostrarSnackbar('Erro: ID do grupo não encontrado.', 'error');
				return;
			}

			if (user.liderancaConvidada) {
				this.mostrarSnackbar('Convite de liderança já foi enviado para este usuário.', 'info');
				return;
			}

			this.convidandoLideranca = true;

			try {
				// Criar convite de liderança
				// Como o campo 'tipo' não é suportado, vamos usar apenas os campos padrão
				// A identificação será feita verificando se o usuário tem plano legislativo
				const conviteLideranca = {
					status: 'pendente',
					id_grupo: this.grupoId,
					id_usuario: user.id,
				};

				const response = await this.$http.post('items/add_grupos', conviteLideranca);

				if (response.data.data && response.data.data.id) {
					user.liderancaConvidada = true;
					this.mostrarSnackbar(
						`Convite de liderança enviado para ${user.first_name}!`,
						'success'
					);
				}
			} catch (error) {
				// Erro ao enviar convite de liderança
				this.mostrarSnackbar('Erro ao enviar convite de liderança. Tente novamente.', 'error');
			} finally {
				this.convidandoLideranca = false;
			}
		},
	},
};
</script>

<style scoped>
.chip-with-hover:hover {
	border: 1px solid #F15A24;
	border-radius: 8px;
	background-color: #fff5f2;
}
</style>


